import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { getProductById } from 'api/generated/product-controller/product-controller';
import type { ProductRequest, ProductRequestType } from 'api/generated/schemas';

import IncludedReports from 'containers/admin/products/product/included-reports/included-reports';
import ProductForm, {
  type ProductFormValues,
  type ProductTypeOption,
} from 'containers/admin/products/product/product-form';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import Button from 'components/partials/button/button';
import { Loader } from 'ui/loader';
import toast from 'components/partials/toast/toast';
import { Typography } from 'ui/typography';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import ActionMenu from 'components/partials/action-menu/action-menu';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';

import { useCreateProduct, useModifyProduct } from 'hooks/api/use-products';
import { useDictionaryByType } from 'hooks/use-dictionary-by-type';

import { useCurrentUser } from 'hooks/api/use-current-user';

interface AdminProductProps {
  productId: string;
}

const AdminProduct = ({ productId }: AdminProductProps): React.JSX.Element => {
  const user = useCurrentUser();
  const userId = (user?.data?.oktaUserId ?? '') as string;
  const navigate = useNavigate();
  const isNew = productId === 'new';

  const createProductMutation = useCreateProduct();
  const modifyProductMutation = useModifyProduct();

  const [productData, setProductData] = useState<ProductFormValues | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(!isNew);
  const [productTypes, setProductTypes] = useState<ProductTypeOption[]>([]);

  const { control, handleSubmit, reset, setFocus, clearErrors } =
    useForm<ProductFormValues>({
      defaultValues: {
        name: '',
        description: '',
        type: '',
      },
    });

  const productTypesDictionaryQuery = useDictionaryByType(
    userId,
    'PRODUCT_TYPE'
  );

  useEffect(() => {
    setFocus('name');
  }, [disabled, setFocus]);

  useEffect(() => {
    const list = productTypesDictionaryQuery.data;

    if (list) {
      setProductTypes(
        list
          .filter((entry) => entry.name)
          .map((entry) => ({
            name: entry.name ?? '',
            description: entry.description ?? '',
          }))
      );
    }
  }, [productTypesDictionaryQuery.data]);

  useEffect(() => {
    if (!isNew) {
      const fetchProduct = async (): Promise<void> => {
        setLoading(true);
        try {
          const dto = await getProductById(userId, Number(productId));

          const name = dto.name ?? '';
          const description = dto.description ?? '';
          const type = String(dto.type ?? '');

          setProductData({ name, description, type });
          reset({ name, description, type });
        } catch (error) {
          console.error('Error while fetching product data', error);
        } finally {
          setLoading(false);
        }
      };

      fetchProduct();
    } else {
      setLoading(false);
    }
  }, [productId, isNew, reset, userId]);

  const buildProductRequest = ({
    name,
    description,
    type,
  }: ProductFormValues): ProductRequest => ({
    name,
    description,
    ...(type ? { type: type as ProductRequestType } : {}),
  });

  const onSubmit = async (data: ProductFormValues): Promise<void> => {
    setLoading(true);
    try {
      if (isNew) {
        await createProductMutation.mutateAsync({
          userId,
          productData: buildProductRequest(data),
        });
        navigate('/admin/products');
      } else {
        await modifyProductMutation.mutateAsync({
          userId,
          productId,
          productData: buildProductRequest(data),
        });
        setProductData(data);
        setDisabled(true);
      }
      toast.success({
        title: `The product was successfully ${isNew ? 'created' : 'updated'}`,
      });
    } catch (error) {
      toast.error({ title: 'Something went wrong' });
      console.error('Error while saving product', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (): void => {
    if (isNew) {
      navigate('/admin/products');
    } else {
      setDisabled(true);
      reset(productData ?? undefined);
    }
  };

  const productTitle = isNew ? 'New Product' : productData?.name;

  return (
    <ResponsiveWrapper customClass="my-4">
      <Breadcrumbs
        breadcrumbs={[
          { label: 'Products', href: '/admin/products' },
          { label: productTitle ?? '', isActive: true },
        ]}
      />
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}
      >
        <Typography data-testid="admin-product-heading" variant="h1">
          {productTitle}
        </Typography>
        {!isNew && (
          <ActionMenu
            buttonName="Edit product"
            onEditClick={() => setDisabled(false)}
            isActionMenuDisabled={!disabled}
          />
        )}
      </Stack>

      <Loader loading={loading} />

      <Typography
        data-testid="admin-product-details-heading"
        variant="h3"
        sx={{ mt: 2.5, mb: 2 }}
      >
        Product details
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <ProductForm
          control={control}
          disabled={disabled}
          productTypes={productTypes}
          clearErrors={clearErrors}
        />

        {(!disabled || isNew) && (
          <Stack direction="row" sx={{ mt: 2, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="reserve-text"
              onClick={handleCancel}
              data-testid="cancel-product-button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-filled"
              data-testid="save-product-button"
            >
              {isNew ? 'Add Product' : 'Save Changes'}
            </Button>
          </Stack>
        )}
      </form>

      {!isNew && (
        <Box sx={{ mt: 4 }}>
          <Typography
            data-testid="admin-product-included-reports-heading"
            variant="h3"
          >
            Included reports
          </Typography>
          <IncludedReports
            productId={productId}
            userId={userId}
            initialLoading={loading}
          />
        </Box>
      )}
    </ResponsiveWrapper>
  );
};

export default AdminProduct;
